import './appCheckDebugEarly'
import { initializeApp } from 'firebase/app'
import { getAuth } from 'firebase/auth'
import { getFirestore } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'
import { getFunctions } from 'firebase/functions'
import { getStoredRegion, REGIONS } from '@/utils/regionUtils'
import { initFirebaseAppCheck } from '@/firebase/appCheck'

const activeRegion = getStoredRegion()

const usConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  functionsUrl: import.meta.env.VITE_FIREBASE_FUNCTION_API_URL,
  region: import.meta.env.VITE_FIREBASE_REGION || 'us-central1'
}

const caConfig = {
  apiKey: import.meta.env.VITE_CA_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_CA_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_CA_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_CA_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_CA_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_CA_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_CA_FIREBASE_MEASUREMENT_ID,
  functionsUrl: import.meta.env.VITE_CA_FIREBASE_FUNCTION_API_URL,
  region: import.meta.env.VITE_CA_FIREBASE_REGION || 'northamerica-northeast1'
}

const usApp = initializeApp(usConfig)
const hasCaConfig = Boolean(caConfig.apiKey)
const caApp = hasCaConfig ? initializeApp(caConfig, 'canada') : null

const hasDebugToken = Boolean(
  import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN?.trim()
)

// Always init App Check only for the active region.
// Initializing both regions can trigger 403 on the inactive project and cause SDK throttling.
if (activeRegion === REGIONS.CA && caApp) {
  initFirebaseAppCheck(caApp, 'ca')
} else {
  initFirebaseAppCheck(usApp, 'us')
}

const regionInstances = {
  [REGIONS.US]: {
    auth: getAuth(usApp),
    db: getFirestore(usApp),
    storage: getStorage(usApp),
    functions: getFunctions(usApp, usConfig.region),
    config: usConfig
  }
}

if (caApp) {
  regionInstances[REGIONS.CA] = {
    auth: getAuth(caApp),
    db: getFirestore(caApp),
    storage: getStorage(caApp),
    functions: getFunctions(caApp, caConfig.region),
    config: caConfig
  }
}

const active = regionInstances[activeRegion] || regionInstances[REGIONS.US]
const firebaseApp =
  activeRegion === REGIONS.CA && caApp ? caApp : usApp

const auth = active.auth
const db = active.db
const storage = active.storage
const functions = active.functions
const firebaseConfig = active.config

const usDb = regionInstances[REGIONS.US].db
const usStorage = regionInstances[REGIONS.US].storage

export {
  auth,
  db,
  storage,
  functions,
  firebaseConfig,
  firebaseApp,
  regionInstances,
  usDb,
  usStorage
}
